import React, { FC, useEffect } from 'react';
import { Controller, UseFormReturn, FieldValues } from 'react-hook-form';
import { get } from 'lodash';
import { FormDataType, PreferredMethod } from 'contexts/FormProvider';
import { select } from 'resources/customerProperties';
import { InformationPopUp, AddressAutocomplete } from 'components';
import { YesNoList } from 'utils/constants';
import { ContactPreferredMethod } from 'pages/common/components/ContactPreferredMethod';
import useValidNumber from 'hooks/useValidNumber';

import { contactPreferredFields, repContactMethodFields } from '../enum';

import { FormGroupLabel, TextField, Button } from '@qbe/ads-core';
import { FormCard, Toggle } from '@qbe/ads-blocks-forms';
import { useContent, useContentContext } from '@qbe/dfc-content';
import AddressManual from 'components/AddressManual/AddressManual';
import ContactNumber from 'components/ContactNumber/ContactNumber';
import FormSpacer from 'components/FormSpacer/FormSpacer';
import { setFormCompleteEvent, setFormStartEvent } from 'hooks/useAnalytics';
import { CmsAddressSearch } from 'components';
import { useFinancialContactAddressHandler } from 'hooks/useFinancialContactAddressHandler';
import { useFlags } from 'launchdarkly-react-client-sdk';
interface Props {
  form: UseFormReturn<FormDataType>;
  show: boolean;
  showNextButton: boolean;
  isContactDetailsValid: boolean;
}

const ContactDetails: FC<Props> = (props) => {
  const { form, show, showNextButton = false, isContactDetailsValid = false } = props;

  const { DcomCustQasMigrationToggle = true } = useFlags();
  // TODO: Remove QAS migration toggle after DcomCustQasMigrationToggle is disabled in production.s
  const { getTextContent } = useContent({ context: useContentContext() });
  const {
    register,
    watch,
    setValue,
    getValues,
    trigger: triggerValidation,
    clearErrors: clearError,
    formState: { errors },
    control,
  } = form;

  const values: object = watch();

  const addressType = watch('addressType') || '';
  const mPhoneNumber = watch('mPhoneNumber') || '';
  const hPhoneNumber = watch('hPhoneNumber') || '';
  const oPhoneNumber = watch('oPhoneNumber') || '';
  const repMPhoneNumber = watch('repMPhoneNumber') || '';
  const repHPhoneNumber = watch('repHPhoneNumber') || '';
  const repOPhoneNumber = watch('repOPhoneNumber') || '';
  const repContactMethod = watch('repContactMethod') || '';
  const nominatedRepType = watch('nominatedRepType') || '';
  const iContactPreferedMethod = watch('iContactPreferedMethod') || '';
  const isComplaint = watch('lodgement') === 'Complaint';

  const showPostalAddress = addressType === 'no';
  const hasNominatedRep = nominatedRepType === 'yes';

  const requiredCheckerNew = (field: PreferredMethod) => iContactPreferedMethod === field;
  const getRepContactMethod = (value: PreferredMethod) => repContactMethod === value;

  // Use address handler hook for address-specific logic
  const {
    isManualPhysAddress,
    isPhysicalCountryAU,
    phyCountry,
    phyState,
    phyPostcode,
    isManualPosAddress,
    isPostalCountryAU,
    posCountry,
    posState,
    posPostcode,
    isManualRepPosAddress,
    isRepPosCountryAU,
    repPosCountry,
    repPosState,
    repPosPostcode,
    handleAddressChange,
    physicalDefaults,
    postalDefaults,
    repPostalDefaults,
  } = useFinancialContactAddressHandler({
    register,
    triggerValidation,
    watch,
    getValues,
    setValue,
    clearErrors: clearError,
    errors: errors as FieldValues,
    DcomCustQasMigrationToggle,
    nominatedRepType,
  });

  // Register non-address form fields
  useEffect(() => {
    register('lodgement');
    register('iContactPreferedMethod');
    register('cEmailAddress');
    register('addressType');
    register('mPhoneNumber');
    register('hPhoneNumber');
    register('oPhoneNumber');
    register('nominatedRepType');
    register('repFirstName');
    register('repLastName');
    register('repContactMethod');
    register('repEmailAddress');
    register('repMPhoneNumber');
    register('repHPhoneNumber');
    register('repOPhoneNumber');
  }, [register]);

  useValidNumber(
    {
      mPhoneNumber,
      hPhoneNumber,
      oPhoneNumber,
      preferredMethod: iContactPreferedMethod,
      haveOneValidNumber: 'haveOneValidNumber',
    },
    form
  );

  useValidNumber(
    {
      mPhoneNumber: repMPhoneNumber,
      hPhoneNumber: repHPhoneNumber,
      oPhoneNumber: repOPhoneNumber,
      preferredMethod: repContactMethod,
      haveOneValidNumber: 'haveOneValidRepNumber',
    },
    form
  );

  const handleContactMethod = (contactMethod: keyof FormDataType, data: string[]) => (method: string) => {
    setValue(contactMethod, method);

    data.forEach((field: string) => {
      if (get(values, field) && get(values, field).length > 0) {
        triggerValidation(field as keyof FormDataType);
        setFormStartEvent();
      } else {
        clearError(field as keyof FormDataType);
      }

      return field;
    });
  };

  if (!show) return null;

  function handleClick(): void {
    setFormCompleteEvent();
  }

  // Localized content constants
  const sectionHeading = getTextContent('consent.contactDetails.heading');
  const preferedContactTypeTitle = getTextContent('consent.contactDetails.preferedContactType');
  const preferedContactTypeInfoText = getTextContent('Financial.contactDetails.preferedContactType.infoText');
  const emailTitle = getTextContent('consent.contactDetails.email');
  const physicalAddressTitle = getTextContent('Financial.details.physicalAddress');
  const addressTypeTitle = getTextContent('Financial.details.addressType');
  const phoneNumberTitle = getTextContent('consent.contactDetails.phoneNumber');
  const nominatedRepTypeTitle = getTextContent('Financial.details.nominatedRepType');
  const repNameTitle = getTextContent('Financial.details.repName');
  const repContactMethodTitle = getTextContent('Financial.details.repContactMethod');
  const repPreferedContactInfoText = getTextContent('Financial.contactDetails.repPreferedContactType.infoText');
  const contactNumberTitle = getTextContent('Financial.details.contactNumber');

  return (
    <FormCard title={sectionHeading} variant="sectionTitle">
      <input type="hidden" {...register('iContactPreferedMethod')} />
      <ContactPreferredMethod
        name="iContactPreferedMethod"
        title={preferedContactTypeTitle}
        titleSuffix={<InformationPopUp message={preferedContactTypeInfoText} />}
        active={iContactPreferedMethod}
        onChange={handleContactMethod('iContactPreferedMethod', contactPreferredFields)}
      />

      <FormSpacer type="input" />

      <FormGroupLabel title={emailTitle} required={requiredCheckerNew('email')} alignCenter />
      <FormSpacer type="input" />
      <Controller
        name="cEmailAddress"
        control={control}
        defaultValue=""
        render={({ field }) => (
          <TextField
            {...field}
            error={!!errors.cEmailAddress}
            value={field.value as string}
            label="Email address"
            id="cEmailAddress"
            data-track="cEmailAddress"
            data-track-value="mask"
            maxLength={100}
            helperText={errors.cEmailAddress ? errors.cEmailAddress?.message : null}
          />
        )}
      />

      <FormSpacer type="input" />

      {/** physical address */}
      <FormGroupLabel title={physicalAddressTitle} alignCenter />
      <FormSpacer type="input" />
      {DcomCustQasMigrationToggle ? (
        <CmsAddressSearch
          type="physical"
          required={isComplaint}
          defaultAddress={physicalDefaults.addressObject ?? undefined}
          defaultManualAddress={physicalDefaults.defaultManualAddress ?? undefined}
          isManualAddress={physicalDefaults.isManual}
          onAddressChange={handleAddressChange}
        />
      ) : (
        <>
          {!isManualPhysAddress && (
            <AddressAutocomplete
              form={form}
              label="Residential address"
              fieldName="physicalAddress"
              fieldNameIsValid="isPhysAddressValid"
              fieldNameIsManual="isManualPhysAddress"
              dataTrackValue="mask"
              isOptional={!isComplaint}
              linkType="physical"
              addressFieldNames={{
                country: 'phyCountry',
                streetAddress: 'phyStreetAddress',
                suburb: 'phySuburb',
                state: 'phyState',
                postCode: 'phyPostcode',
              }}
            />
          )}

          {/** Physical Manual Adddress fields */}
          {/* eslint-disable-next-line @typescript-eslint/no-explicit-any */}
          {isManualPhysAddress && <AddressManual prefix="phy" form={form} />}
        </>
      )}
      <FormSpacer type="input" />

      {/** addressType */}
      <Controller
        name="addressType"
        control={control}
        defaultValue={addressType}
        render={({ field }) => (
          <Toggle
            {...field}
            title={addressTypeTitle}
            required={requiredCheckerNew('post')}
            id="addressType"
            data-track="compType"
            data-track-value="capture"
            optionList={YesNoList.map(({ text, value }) => {
              return {
                id: `addressType_${value}`,
                label: text,
                value,
                defaultChecked: addressType === value,
              };
            })}
            onChange={(e) => {
              const { value } = e.target;

              setValue('addressType', value, { shouldValidate: true });
              triggerValidation('addressType');

              if (value === 'yes') {
                setValue('postalAddress', null, { shouldValidate: true });
                setValue('isManualPosAddress', false, { shouldValidate: true });
                setValue('postalAddressObject', null, { shouldValidate: true });
                setValue('posStreetAddress', '', { shouldValidate: true });
                setValue('posSuburb', '', { shouldValidate: true });
                setValue('posCountry', '', { shouldValidate: true });
                setValue('posState', '', { shouldValidate: true });
                setValue('posPostcode', '', { shouldValidate: true });
              }

              field.onChange(value);
            }}
          />
        )}
      />

      <FormSpacer type="input" />

      {/** postalAddress */}
      {DcomCustQasMigrationToggle ? (
        showPostalAddress && (
          <CmsAddressSearch
            type="postal"
            required={isComplaint}
            defaultAddress={postalDefaults.addressObject ?? undefined}
            defaultManualAddress={postalDefaults.defaultManualAddress ?? undefined}
            isManualAddress={postalDefaults.isManual}
            onAddressChange={handleAddressChange}
          />
        )
      ) : (
        <>
          {showPostalAddress && !isManualPosAddress && (
            <AddressAutocomplete
              form={form}
              label="Postal address"
              fieldName="postalAddress"
              fieldNameIsValid="isPosAddressValid"
              fieldNameIsManual="isManualPosAddress"
              dataTrackValue="mask"
              isOptional={!isComplaint}
              linkType="postal"
              addressFieldNames={{
                country: 'posCountry',
                streetAddress: 'posStreetAddress',
                suburb: 'posSuburb',
                state: 'posState',
                postCode: 'posPostcode',
              }}
            />
          )}

          {/* eslint-disable-next-line @typescript-eslint/no-explicit-any */}
          {showPostalAddress && isManualPosAddress && <AddressManual prefix="pos" form={form} />}
        </>
      )}

      {/** contactNumber */}
      <FormGroupLabel title={phoneNumberTitle} required={requiredCheckerNew('phone')} alignCenter />
      <FormSpacer type="input" />
      <ContactNumber form={form} />

      <FormSpacer type="input" />

      {/** nominatedRepType */}
      <Controller
        rules={{ required: '' }}
        control={control}
        defaultValue={nominatedRepType}
        name="nominatedRepType"
        render={({ field }) => (
          <Toggle
            {...field}
            title={nominatedRepTypeTitle}
            required
            data-track="compType"
            data-track-value="capture"
            optionList={YesNoList.map(({ value, text }) => {
              return {
                id: `nominatedRepType_${value}`,
                label: text,
                value,
                checked: nominatedRepType === value,
              };
            })}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
              const { value } = e.target;
              setValue('nominatedRepType', value, { shouldValidate: true });
              field.onChange(value);
            }}
          />
        )}
      />

      {hasNominatedRep && (
        <>
          {/** repName */}
          <FormGroupLabel required title={repNameTitle} alignCenter />
          <FormSpacer type="input" />
          <Controller
            name="repFirstName"
            control={control}
            defaultValue=""
            render={({ field }) => (
              <TextField
                {...field}
                error={!!errors.repFirstName}
                label="First name"
                id="repFirstName"
                value={field.value as string}
                data-track="repFirstName"
                data-track-value="mask"
                inputProps={{
                  maxLength: 50,
                  minLength: 1,
                }}
                data-test="repFirstName"
                helperText={errors.repFirstName ? errors.repFirstName?.message : null}
              />
            )}
          />

          <FormSpacer type="input" />

          <Controller
            name="repLastName"
            control={control}
            defaultValue=""
            render={({ field }) => (
              <TextField
                {...field}
                error={!!errors.repLastName}
                value={field.value as string}
                label="Last name"
                id="repLastName"
                data-track="repLastName"
                data-track-value="mask"
                inputProps={{
                  maxLength: 50,
                  minLength: 1,
                }}
                data-test="repLastName"
                helperText={errors.repLastName ? errors.repLastName?.message : null}
              />
            )}
          />
          <FormSpacer type="formgroup" />

          {/** repContactMethod */}
          <input type="hidden" {...register('repContactMethod')} />
          <ContactPreferredMethod
            name="repContactMethod"
            title={repContactMethodTitle}
            titleSuffix={<InformationPopUp message={repPreferedContactInfoText} />}
            active={repContactMethod}
            onChange={handleContactMethod('repContactMethod', repContactMethodFields)}
          />

          {/** repEmailAddress */}
          {getRepContactMethod('email') && (
            <>
              <FormSpacer type="formgroup" />
              <FormGroupLabel required title={select('Financial.details.emailAddress')} alignCenter />
              <FormSpacer type="input" />
              <Controller
                name="repEmailAddress"
                control={control}
                defaultValue=""
                render={({ field }) => (
                  <TextField
                    {...field}
                    value={field.value as string}
                    error={!!errors.repEmailAddress}
                    label="Email address"
                    id="repEmailAddress"
                    data-track="repEmailAddress"
                    data-track-value="mask"
                    maxLength={100}
                    helperText={errors.repEmailAddress ? errors.repEmailAddress?.message : null}
                  />
                )}
              />
            </>
          )}
          {/** repPostalAddress */}
          {getRepContactMethod('post') && (
            <>
              <FormSpacer type="formgroup" />
              <FormGroupLabel required title={select('Financial.details.postalAddress')} alignCenter />
              <FormSpacer type="input" />
              {DcomCustQasMigrationToggle ? (
                <CmsAddressSearch
                  type="repPostal"
                  required={isComplaint}
                  defaultAddress={repPostalDefaults.addressObject ?? undefined}
                  defaultManualAddress={repPostalDefaults.defaultManualAddress ?? undefined}
                  isManualAddress={repPostalDefaults.isManual}
                  onAddressChange={handleAddressChange}
                />
              ) : (
                <>
                  {!isManualRepPosAddress && (
                    <AddressAutocomplete
                      form={form}
                      label="Postal address"
                      fieldName="repPostalAddress"
                      fieldNameIsValid="isRepPosAddressValid"
                      fieldNameIsManual="isManualRepPosAddress"
                      dataTrackValue="mask"
                      isOptional={!isComplaint}
                      linkType="repPostal"
                      addressFieldNames={{
                        country: 'repPosCountry',
                        streetAddress: 'repPosStreetAddress',
                        suburb: 'repPosSuburb',
                        state: 'repPosState',
                        postCode: 'repPosPostcode',
                      }}
                    />
                  )}

                  {/* eslint-disable-next-line @typescript-eslint/no-explicit-any */}
                  {isManualRepPosAddress && <AddressManual prefix="repPos" form={form} />}
                </>
              )}
            </>
          )}
          {/** repContactNumber */}
          {getRepContactMethod('phone') && (
            <>
              <FormSpacer type="formgroup" />
              <FormGroupLabel required title={contactNumberTitle} alignCenter />
              <FormSpacer type="input" />
              <ContactNumber prefix="rep" form={form} />
            </>
          )}
        </>
      )}

      <FormSpacer type="formgroup" />

      {showNextButton && (
        <Button type="submit" onClick={handleClick} label="Continue" disabled={!isContactDetailsValid} />
      )}
    </FormCard>
  );
};

export default ContactDetails;
